<template>
  <div
    class="emergency-driver-view"
    :class="{ 'emergency-selection-active': hasSolutions }"
  >
    <div class="emergency-driver-container">
      <div class="scroll-window">
        <vue-perfect-scrollbar
          class="scroll-area"
          id="emergency-driver-scroll"
          ref="emergencyDriverScroll"
        >
          <easy-minimize
            class="emergency-driver"
            ref="emergencyDriverPanel"
            :title-heading="'Emergency Drivers'"
            :title-minimized="'Emergency'"
            :total="emergencyDrivers.length"
            :direction="'left'"
            :showBack="true"
            @backClicked="backClicked"
          >
            <accordion>
              <accordion-item
                :title="'Calculate performance'"
                :openOnLoad="true"
                v-if="isBodine"
                @toggleContent="updateScrollbar"
              >
                <emergency-driver-calculation-form />
              </accordion-item>
              <accordion-item
                :threeDot="true"
                :threeDotItems="[{ id: 'reset', displayText: 'Reset' }]"
                :additionalActionItems="[
                  {
                    id: 'showAllActiveDriverFilters',
                    displayText: 'All active filters',
                  },
                ]"
                :title="'Filters'"
                :btnText="'Clear filters'"
                @clearFiltersClicked="onClearFiltersClicked"
                @threeDotItemClicked="threeDotItemClicked"
                @additionalActionItemClicked="additionalActionItemClicked"
                :showAdditionalItems="hasActiveEmergencyDriverFilters"
                :openOnLoad="!isBodine"
                @toggleContent="updateScrollbar"
              >
                <div class="search-container">
                  <text-field
                    placeholder="Filter on part of name, 12NC, etc."
                    @keypress.enter="onSearchInputChange($event)"
                    :value="emergencyDriverSearchTerm"
                  />
                </div>
                <easy-filters
                  @change="onFilterChange"
                  @clear="onClear(...arguments)"
                  :filters="filtersToShow"
                  :distanceUnit="distanceUnit"
                  @toggleFilter="updateScrollbar"
                />
              </accordion-item>
            </accordion>
          </easy-minimize>
        </vue-perfect-scrollbar>
      </div>
      <div class="results-block">
        <div class="emergency-driver-table">
          <easy-table-options-bar
            v-if="isBodine"
            :options="tableOptions"
            :activeOption="emergencyDriverActiveTableOptionTab"
            :columnsVisibilty="resultEmergencyDriverColumnSet"
            :pinnedItems="[]"
            @setActive="emergencyDriverOptionBarAction"
            @columnsVisibilityChange="onColumnVisibilityChange($event)"
          />
          <easy-table
            ref="emergencyDriverTable"
            id="emergency-driver-table"
            :columnDefs="emergencyDriverColumnDefs"
            :defaultColDefs="{ resizable: true }"
            :headerHeight="40"
            :rowHeight="32"
            :rowData="emergencyDrivers"
            :visibleColumns="resultEmergencyDriverColumnSet"
            :eventBus="EventBus"
            :activeProduct="emergencyDriver"
            :emergencyDriver="true"
            :hasActiveSolution="!!solution"
            @selectRow="onSelectEmergencyDriverRow"
            @ctaAddToSolution="
              onComponentAddToSolution($event, 'emergency-driver')
            "
            @ctaMoreInfo="onComponentMoreInfo($event, 'emergency-driver')"
            @ctaCopyToClipboard="
              onComponentCopyToClipboard($event, 'emergency-driver')
            "
            @newColumnsLoaded="resizeColumns"
            @ctaCopyLink="onComponentCopyLink($event, 'driver')"
          />
        </div>
      </div>
    </div>
    <easy-selection
      class="solution"
      :solutions="solutions"
      :active="active"
      :region="region"
      :onlyEmergency="true"
      @viewSolution="onViewSolution"
      @removeEmergencyDriver="onRemoveEmergencyDriver"
    />
  </div>
</template>
<script>
import { cloneDeep } from "lodash";
import { mapGetters, mapState } from "vuex";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { CellRenderer } from "@/utils/ColumnMapping";
import { EventBus } from "@/eventbus.js";
import FilterListModal from "@/components/modals/FilterListModal";
import ComponentDetailModal from "@/components/modals/ComponentDetailModal";
import SolutionModal from "@/components/modals/SolutionModal";
import EmergencyDriverCalculationForm from "@/components/EmergencyDriverCalculationForm";

export default {
  name: "EmergencyDriverView",
  modals: {
    ComponentDetailModal,
    SolutionModal,
    FilterListModal,
  },
  data() {
    return {
      emergencyDriverPanelHeight: "auto",
      emergencyDrivers: [],
      EventBus,
      emergencyDriverActiveTableOptionTab: null,
    };
  },
  components: {
    VuePerfectScrollbar,
    EmergencyDriverCalculationForm,
  },
  props: {
    searchItem: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters("brain/ui", ["minimizeEmergencyDriver", "baseLayoutMounted"]),

    ...mapGetters("brain/ui", {
      standardEmergencyDriverColumnVisibility:
        "standardEmergencyDriverColumnVisibility",
      savedEmergencyDriverColumnVisibility:
        "savedEmergencyDriverColumnVisibility",
    }),

    ...mapGetters("brain/auth", ["isLoggedIn"]),

    ...mapState("brain", ["distanceUnit", "region"]),

    ...mapState("brain/emergencyDrivers", {
      initialEmergencyDriverFilters: "initialFilters",
      emergencyDriverFilters: "filters",
      emergencyDriver: "driver",
      emergencyDriverSearchTerm: "emergencyDriverSearchTerm",
      pinnedEmergencyDrivers: "pinnedEmergencyDrivers",
    }),

    ...mapGetters("brain/emergencyDrivers", {
      initialEmergencyDriverColumnDefs: "initialColumnDefs",
      emergencyDriverColumnDefs: "columnDefs",
    }),

    ...mapGetters("brain/solutions", [
      "hasSolutions",
      "solutions",
      "solution",
      "active",
      "selectedEmergencyDrivers",
    ]),

    isBodine() {
      return this.region === "na";
    },

    hasActiveEmergencyDriverFilters() {
      return this.hasActiveFilters(this.emergencyDriverFilters);
    },

    solutionModule() {
      if (this.solution) {
        return this.solution.module != null ? this.solution.module : null;
      }
      return null;
    },

    solutionDriver() {
      if (this.solution) {
        return this.solution.driver != null ? this.solution.driver : null;
      }
      return null;
    },

    resultEmergencyDriverColumnSet() {
      let standardColumnSet = cloneDeep(
        this.standardEmergencyDriverColumnVisibility
      );
      const savedColumnSet = this.savedEmergencyDriverColumnVisibility;
      savedColumnSet.forEach((col) => {
        const index = standardColumnSet.findIndex(
          (standardCol) => standardCol.headerName === col.headerName
        );
        if (index !== -1) {
          standardColumnSet[index].value = col.value;
        }
      });
      return standardColumnSet;
    },

    tableOptions() {
      const options = [
        { icon: "download", title: "Download" },
        { icon: "compare", title: "Compare", disabled: true },
      ];

      if (this.canI("TOGGLE_VISIBLE_COLUMNS")) {
        options.unshift({ icon: "columns", title: "Columns" });
      }

      return options;
    },
    filtersToShow() {
      return this.emergencyDriverFilters
        .filter((filter) => filter.filters[0].availableValues !== undefined)
        .filter((filter) => filter.title !== "Exceptions");
    },
    onlyHV() {
      return (
        !this.isBodine &&
        this.hasSolutions &&
        this.solution.driver !== null &&
        this.solution.driver.voltageType === "High voltage"
      );
    },
    onlyLV() {
      return (
        !this.isBodine &&
        this.hasSolutions &&
        this.solution.driver !== null &&
        this.solution.driver.voltageType === "Low voltage"
      );
    },
    selectedEmergencyDrivers() {
      if (
        !this.isBodine &&
        this.hasSolutions &&
        this.solution.emergencyDrivers.length > 0
      ) {
        return this.solution.emergencyDrivers;
      }
      return null;
    },
  },
  created() {
    this.getEmergencyDriverColumnDefs();
    this.$store.commit("brain/ui/showControls", false);

    EventBus.$on("recalculate", (page) => {
      if (page === "emergencyDriver") {
        this.calculate();
      }
    });

    EventBus.$on(
      "handleFilterChange",
      ({ data = null, addToHistory = true }) => {
        this.onFilterChange(data, addToHistory);
      }
    );

    window.addEventListener("resize", this.handleResize);

    if (this.searchItem) {
      this.onClearFiltersClicked();
      this.onSearchInputChange(this.searchItem.nc12);
      this.calculate();
    } else {
      this.calculate();
    }
  },
  mounted() {
    this.handleResize();
  },
  methods: {
    updateScrollbar() {
      setTimeout(() => {
        this.$nextTick(() => {
          this.$refs.emergencyDriverScroll.update();
        });
        // Wait for animation to complete before updating scrollbar
      }, 250);
    },

    calculate() {
      this.$store
        .dispatch("brain/emergencyDrivers/getEmergencyDrivers")
        .then((emergencyDrivers) => {
          if (this.selectedEmergencyDrivers) {
            const selectedEmergencyDriverName =
              this.selectedEmergencyDrivers[0].name;
            if (selectedEmergencyDriverName.includes("NiMH")) {
              this.emergencyDrivers = emergencyDrivers.filter(
                (emergencyDriver) => emergencyDriver.name.includes("NiMH")
              );
              this.$store.dispatch(
                "brain/emergencyDrivers/disableFilter",
                "TrustSight Gen3 Batteries LFP"
              );
            } else if (selectedEmergencyDriverName.includes("LFP")) {
              this.emergencyDrivers = emergencyDrivers.filter(
                (emergencyDriver) => emergencyDriver.name.includes("LFP")
              );
              this.$store.dispatch(
                "brain/emergencyDrivers/disableFilter",
                "TrustSight Gen3 Batteries NiMH"
              );
            } else {
              this.emergencyDrivers = emergencyDrivers;
            }
          } else {
            this.emergencyDrivers = emergencyDrivers;
          }

          // Check solution driver
          if (this.onlyHV) {
            this.onFilterChange(
              {
                name: "driverFamilyFilter_3",
                values: ["TrustSight Gen3 High Voltage"],
              },
              false
            );
          } else if (this.onlyLV) {
            this.onFilterChange(
              {
                name: "driverFamilyFilter_3",
                values: [
                  "TrustSight Gen3 Indoor Emergency Systems - Low Voltage",
                ],
              },
              false
            );
          }
        });
    },

    onSearchInputChange(e) {
      let value = typeof e === "string" ? e : e.target.value;
      this.$store.commit(
        "brain/emergencyDrivers/setEmergencyDriverSearchTerm",
        value
      );
      this.onFilterChange();
    },

    async getEmergencyDriverColumnDefs() {
      const columnDefs = cloneDeep(this.initialEmergencyDriverColumnDefs);
      const columnSet = cloneDeep(this.resultEmergencyDriverColumnSet);
      const emergencyDriverColumnDefs = this.getColumnDefs(
        columnDefs,
        columnSet,
        "emergency-driver"
      );

      await this.$store.commit(
        "brain/emergencyDrivers/setColumnDefs",
        emergencyDriverColumnDefs
      );
    },

    getColumnDefs(columnDefs, columnSet, type) {
      columnSet.map((column) => {
        if (!column.value) {
          const indexToHide = columnDefs.findIndex(
            (x) => x.field === column.name
          );
          columnDefs[indexToHide].hide = true;
        }
      });

      const visibleColumns = columnSet.filter((column) => column.value);
      if (visibleColumns.length) {
        const firstVisibleColumnName = visibleColumns[0].name;
        const firstColumnIndex = columnDefs.findIndex((column) => {
          return column.field === firstVisibleColumnName;
        });

        columnDefs[firstColumnIndex].cellRenderer = null;
        columnDefs[firstColumnIndex].cellRendererFramework = CellRenderer;
        columnDefs[firstColumnIndex].cellRendererParams = {
          type: type,
          field: columnDefs[firstColumnIndex].field,
        };
      }
      return columnDefs;
    },

    emergencyDriverOptionBarAction(option) {
      if (
        this.emergencyDriverActiveTableOptionTab !== option.title &&
        option.title !== "Download"
      ) {
        this.emergencyDriverActiveTableOptionTab = option.title;
      } else {
        this.emergencyDriverActiveTableOptionTab = null;
      }
      if (option.title === "Download") {
        if (!this.isLoggedIn) {
          this.$snotify.error(
            "Downloading drivers is only available for logged in users"
          );
        } else {
          this.$store.dispatch(
            "brain/emergencyDrivers/downloadEmergencyDrivers"
          );
        }
      }
    },

    onColumnVisibilityChange(e) {
      this.$store.commit("brain/ui/setEmergencyDriverColumnVisibility", {
        value: e,
        save: true,
      });
    },

    onSelectEmergencyDriverRow(selectedRow) {
      this.$store.commit(
        "brain/emergencyDrivers/setEmergencyDriver",
        selectedRow.data
      );
      this.$store.commit("brain/global/setActiveComponent", "emergency-driver");
    },

    onComponentAddToSolution(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      this.setComponent({ type: type, data: selectedRow, addNew: false });
    },

    onComponentMoreInfo(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      this.$modalRouter.push({
        name: "ComponentDetailModal",
        on: {
          componentSelectedContinue: this.setComponent,
          onArrowKeyPress: this.selectComponent,
        },
      });
    },
    onComponentCopyToClipboard(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      const text = `${selectedRow.data.name}, ${selectedRow.data.nc12}`;
      this.copyToClipboard(text);
    },
    onComponentCopyLink(selectedRow, type) {
      this.$store.commit("brain/global/setActiveComponent", type);
      const url = `${window.location.origin}/#/share?type=emergency-driver&nc12=${selectedRow.data.nc12}&region=${this.region}`;
      this.copyToClipboard(url);
    },
    copyToClipboard(text) {
      const dummy = document.createElement("input");
      document.body.appendChild(dummy);
      dummy.value = text;
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
    },
    setComponent({ type, data, addNew }) {
      // Check solution driver
      if (
        !this.isBodine &&
        this.hasSolutions &&
        this.solution.driver !== null
      ) {
        // Dont allow to add trustsight if driver is outdoor
        if (this.solution.driver.familyLevelOne === "Outdoor") {
          this.$snotify.error(
            "Your solution contains an Outdoor Driver, which is not compatible with a TrustSight system."
          );
          return;
        }
      }
      this.$store
        .dispatch("brain/solutions/add", { type, addNew, data })
        .then(() => {
          this.calculate();
        });
      if (this.isBodine) {
        this.$router.push({
          name: "advanced",
          params: { openSolutionModal: true },
        });
      }
    },

    selectComponent(props) {
      if (props.direction === "up") {
        this.$refs.emergencyDriverTable.selectPreviousRow();
      } else {
        this.$refs.emergencyDriverTable.selectNextRow();
      }
    },

    onMinimize() {
      this.$store.commit("brain/ui/minimizeInput", {
        type: "emergency-driver",
        minimize: true,
        save: true,
      });
    },

    onMaximize() {
      this.$store.commit("brain/ui/minimizeInput", {
        type: "emergency-driver",
        minimize: false,
        save: true,
      });
    },

    onClearFiltersClicked() {
      this.$store
        .dispatch("brain/emergencyDrivers/resetAllFilters")
        .then((emergencyDrivers) => {
          this.emergencyDrivers = emergencyDrivers;
        });
    },

    onFilterChange(e = null, addToHistory = true) {
      if (this.onlyHV) {
        if (!e.values.length) {
          e.values.push("TrustSight Gen3 High Voltage");
        }
      }

      if (this.onlyLV) {
        if (!e.values.length) {
          e.values.push(
            "TrustSight Gen3 Indoor Emergency Systems - Low Voltage"
          );
        }
      }

      this.$store
        .dispatch("brain/emergencyDrivers/handleFilterChange", {
          data: e,
          addToHistory,
        })
        .then((emergencyDrivers) => {
          if (this.selectedEmergencyDrivers) {
            const selectedEmergencyDriverName =
              this.selectedEmergencyDrivers[0].name;
            if (selectedEmergencyDriverName.includes("NiMH")) {
              this.emergencyDrivers = emergencyDrivers.filter(
                (emergencyDriver) => emergencyDriver.name.includes("NiMH")
              );
              this.$store.dispatch(
                "brain/emergencyDrivers/disableFilter",
                "TrustSight Gen3 Batteries LFP"
              );
            } else if (selectedEmergencyDriverName.includes("LFP")) {
              this.emergencyDrivers = emergencyDrivers.filter(
                (emergencyDriver) => emergencyDriver.name.includes("LFP")
              );
              this.$store.dispatch(
                "brain/emergencyDrivers/disableFilter",
                "TrustSight Gen3 Batteries NiMH"
              );
            } else {
              this.emergencyDrivers = emergencyDrivers;
            }
          } else {
            this.emergencyDrivers = emergencyDrivers;
          }
          if (this.onlyHV) {
            this.$store.dispatch(
              "brain/emergencyDrivers/disableFilter",
              "TrustSight Gen3 Indoor Emergency Systems - Low Voltage"
            );
          }
          if (this.onlyLV) {
            this.$store.dispatch(
              "brain/emergencyDrivers/disableFilter",
              "TrustSight Gen3 High Voltage"
            );
          }
        });
    },

    onClear(group) {
      if (group.filters[0].type === "range") {
        //assumes every filter in group is of same kind
        this.$store.commit(
          "brain/emergencyDrivers/resetSpecificFilterGroup",
          this.resetRangeFilterGroup(group)
        );
        this.$store
          .dispatch("brain/emergencyDrivers/getEmergencyDrivers")
          .then((emergencyDrivers) => {
            this.emergencyDrivers = emergencyDrivers;
          });
      } else {
        this.onFilterChange({
          name: group.filters[0].name,
          values: [],
        });
      }
    },

    resetRangeFilterGroup(group) {
      const toReturn = cloneDeep(group);
      toReturn.filters.forEach((filter) => {
        const lowestValue = Math.min(
          ...filter.availableValues.map((i) => i.value)
        );
        const highestValue = Math.max(
          ...filter.availableValues.map((i) => i.value)
        );
        filter.from = lowestValue;
        filter.to = highestValue;
        filter.value = null;
      });
      return toReturn;
    },

    showAllTags(type) {
      this.$modalRouter.push({
        name: "FilterListModal",
        props: { type: type },
        on: {
          emergencyDriverFilterChange: this.onFilterChange,
          clearEmergencyDriverFiltersClicked: this.onClearFiltersClicked,
        },
      });
    },

    backClicked() {
      this.$store.commit("brain/global/setCheckReload", true);
      this.$router.push({ name: "advanced" });
    },

    handleResize() {
      if (this.$refs.emergencyDriverPanel !== undefined) {
        const actionBarHeight = this.$parent.$refs.actionBar.$el.clientHeight;
        this.$refs.emergencyDriverScroll.$el.style.height =
          window.innerHeight - actionBarHeight - 5 + "px";
      }
    },

    hasActiveFilters(myFilters) {
      let filtersExists = false;
      for (let filters in myFilters) {
        let filter = myFilters[filters].filters;
        for (let f in filter) {
          if (filter[f].value !== null) {
            if (typeof filter[f].value !== "undefined") {
              if (
                typeof filter[f].value === "object" &&
                filter[f].value.length > 0
              ) {
                filtersExists = true;
                break;
              } else if (
                typeof filter[f].value !== "object" &&
                filter[f].value !== ""
              ) {
                filtersExists = true;
                break;
              }
            }
          }
        }
        if (filtersExists) {
          break;
        }
      }
      return filtersExists;
    },

    threeDotItemClicked(item) {
      if (item === "reset") {
        this.onClearFiltersClicked();
      }
    },

    additionalActionItemClicked() {
      this.showAllTags("emergency-driver");
    },
    resizeColumns() {
      setTimeout(
        function () {
          EventBus.$emit("resizeColumns");
        }.bind(EventBus),
        200
      );
    },
    onRemoveEmergencyDriver(index) {
      if (this.solution) {
        this.$store
          .dispatch("brain/solutions/removeEmergencyDriver", index)
          .then(() => {
            this.calculate();
          });
      }
    },
    onViewSolution() {
      this.$modalRouter.push({
        name: "SolutionModal",
        props: {
          solution: this.solution,
        },
      });
    },
  },
  watch: {
    resultEmergencyDriverColumnSet() {
      this.getEmergencyDriverColumnDefs();
    },
  },
};
</script>
<style lang="scss">
.emergency-driver-view {
  display: flex;
  flex-direction: column;
  flex-grow: 2;

  .emergency-driver-container {
    display: flex;
    flex-grow: 2;
    justify-content: space-between;
    align-items: stretch;
  }

  .easy-minimize {
    .content {
      flex-direction: column;
    }
  }

  .scroll-area {
    position: relative;
    margin: 0;
    width: 100%;
    border-right: 1px solid $mid-gray-200;
  }

  .search-container {
    padding: 0 1.5rem;
  }

  .emergency-driver {
    justify-content: flex-end;

    .scroll-window {
      position: relative;
      height: 100%;
    }

    .content {
      height: auto;
      flex-grow: 2;
      justify-content: space-between;
    }
  }

  .results-block {
    display: flex;
    width: 100%;

    .emergency-driver-table {
      flex-grow: 2;
      display: flex;
      flex-direction: column;
      width: 100%;
      .phase-out {
        color: #e98500;
      }

      .phase-in {
        color: #00bfff;
      }

      .pre-cr {
        color: #32cd32;
        white-space: nowrap;
      }

      .is-archived {
        color: #a900ff;
        white-space: nowrap;
      }
    }
  }

  .easy-table {
    .ag-theme-balham {
      .ag-cell {
        line-height: 30px;

        .pin-icon {
          margin-top: -4px;
        }
      }
    }
  }
}

.emergency-selection-active {
  .results-block {
    height: calc(100% - 75px);
  }
}
</style>
